<template>
  <main class="mx-auto mt-4 w-full max-w-5xl space-y-6">
    <header class="flex flex-wrap items-end justify-between gap-3 border-b border-base-300 pb-4">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Process action</p>
        <h1 class="mt-1 text-2xl font-bold">Lịch sử inject</h1>
        <p class="mt-1 text-sm text-base-content/65">Các yêu cầu mới nhất của agent.</p>
      </div>
      <RouterLink class="btn btn-primary" :to="{ name: 'ClientInjectCreate' }">
        <i class="fa-solid fa-plus" aria-hidden="true"></i>
        Tạo yêu cầu inject
      </RouterLink>
    </header>

    <div v-if="isLoading" class="flex justify-center py-10" role="status" aria-label="Đang tải">
      <span class="loading loading-spinner loading-lg text-primary"></span>
    </div>
    <section v-else-if="isError && !actions.length" class="alert alert-error">
      <span>{{ errorMessage }}</span>
      <button class="btn btn-sm" type="button" @click="refetch()">Thử lại</button>
    </section>
    <section v-else-if="!actions.length" class="rounded-lg border border-base-300 bg-base-100 p-8 text-center">
      <p class="font-semibold">Chưa có yêu cầu inject</p>
      <p class="mt-1 text-sm opacity-70">Tạo yêu cầu đầu tiên cho agent này.</p>
      <RouterLink class="btn btn-primary btn-sm mt-4" :to="{ name: 'ClientInjectCreate' }">Tạo yêu cầu</RouterLink>
    </section>
    <template v-else>
      <p class="text-sm opacity-70">{{ totalFiltered.toLocaleString('vi-VN') }} yêu cầu</p>
      <section class="space-y-3" aria-label="Lịch sử inject">
        <article v-for="action in actions" :key="action.id" class="rounded-lg border border-base-300 bg-base-100 p-4">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-2">
              <span class="badge" :class="statusClass(action.status)">{{ statusText(action.status) }}</span>
              <span class="badge badge-outline">{{ injectTypeName(action.type) }}</span>
            </div>
            <time class="text-sm opacity-70">{{ formatTime(action.created) }}</time>
          </div>
          <div class="mt-3 grid gap-1 text-sm md:grid-cols-2">
            <p><span class="font-semibold">PID:</span> <code>{{ action.target_pid || '—' }}</code></p>
            <p class="break-all"><span class="font-semibold">Đường dẫn:</span> {{ action.target_path || '—' }}</p>
          </div>
          <p v-if="action.log" class="mt-3 whitespace-pre-wrap break-words rounded bg-base-200 p-3 text-sm">{{ action.log }}</p>
          <p class="mt-2 break-all text-xs opacity-50">{{ action.id }}</p>
        </article>
      </section>
      <div v-if="isFetchingNextPage" class="flex justify-center py-4" role="status" aria-label="Đang tải thêm">
        <span class="loading loading-spinner text-primary"></span>
      </div>
      <button v-if="isFetchNextPageError" class="btn btn-outline mx-auto block" type="button" @click="fetchNextPage()">Tải thêm lần nữa</button>
      <p v-else-if="!hasNextPage" class="py-3 text-center text-sm opacity-60">Đã tải hết lịch sử</p>
    </template>
  </main>
</template>

<script setup lang="ts">
import { listInjectActions, type InjectActionHistory } from "@/api/action";
import dayjs from "dayjs";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useInfiniteScroll } from "@vueuse/core";
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const agentId = computed(() => String(route.params.client_id ?? ""));
const query = useInfiniteQuery({
  queryKey: computed(() => ["inject-actions", agentId.value]),
  queryFn: ({ pageParam }) => listInjectActions({ agent_id: agentId.value, page: pageParam, size: 20 }),
  initialPageParam: 1,
  getNextPageParam: (lastPage, pages) =>
    pages.length * 20 < lastPage.total_filter ? pages.length + 1 : undefined,
});
const {
  isPending: isLoading,
  isError,
  refetch,
  isFetchingNextPage,
  isFetchNextPageError,
  fetchNextPage,
  hasNextPage,
} = query;
const actions = computed<InjectActionHistory[]>(() => query.data.value?.pages.flatMap((page) => page.data ?? []) ?? []);
const totalFiltered = computed(() => query.data.value?.pages[0]?.total_filter ?? 0);
const errorMessage = computed(() => {
  const response = query.error.value as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  } | null;
  const serverMessage = response?.response?.data?.message ?? response?.response?.data?.error;
  const friendly: Record<string, string> = {
    "agent_id is empty": "Không xác định được agent. Hãy mở lại trang từ hồ sơ agent.",
    "agent not found": "Không tìm thấy agent này.",
    "agent is uninstalled": "Agent đã được gỡ cài đặt; không thể tải lịch sử inject.",
    "administrator or system privilege required": "Agent cần quyền Administrator hoặc SYSTEM để xem lịch sử inject.",
    "missing refresh token": "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
    "refresh token expired": "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
    "failed to generate access token": "Không thể gia hạn phiên đăng nhập. Vui lòng đăng nhập lại.",
    "failed to generate refresh token": "Không thể gia hạn phiên đăng nhập. Vui lòng đăng nhập lại.",
  };
  if (serverMessage) return friendly[serverMessage] ?? serverMessage;
  return response?.message ?? "Không thể kết nối máy chủ. Vui lòng thử lại.";
});

const formatTime = (timestamp: number) => dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
const injectTypeName = (type: number) => ({ 1: "Reflective DLL", 2: "Masquerading", 3: "Process Hollowing" })[type] ?? `Inject ${type}`;
const statusText = (status: string) => ({ pending: "Đang chờ", "in-progress": "Đang xử lý", complete: "Hoàn tất", error: "Lỗi" })[status] ?? status;
const statusClass = (status: string) => ({ pending: "badge-warning", "in-progress": "badge-info", complete: "badge-success", error: "badge-error" })[status] ?? "badge-neutral";

onMounted(() => {
  const scrollContainer = document.querySelector("main");
  if (scrollContainer) {
    useInfiniteScroll(scrollContainer, () => {
      if (query.hasNextPage.value && !query.isFetchingNextPage.value) query.fetchNextPage();
    }, { distance: 120 });
  }
});
</script>
